import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Section } from "../commercial-dashboard/DashboardBits";
import {
  CM_CHANNELS,
  MONTHS,
  MONTHS_SHORT,
  aggregate,
  byPerson,
  eur,
  eurSigned,
  groupBy,
  inPeriod,
  keur,
  pct,
  pctSigned,
  pp,
  subformatLabel,
  sum,
  toneOfAmount,
  toneOfExecution,
  toneOfMarginDiff,
  Totals,
} from "./controlBoardModel";
import {
  Caret,
  ChannelSwatch,
  Note,
  Num,
  PeriodSelector,
  Signal,
  useToneColor,
} from "./ControlBoardBits";
import { TabProps } from "./boardData";
import { ControlBoardChannel, ControlBoardRow } from "../../types/controlBoard";

const clientsOf = (rows: ControlBoardRow[]) =>
  new Set(rows.map((r) => r.clientId)).size;

/** Técnicos: margen que saca cada uno en compra de medios y control de inversión en Paid Media. */
const TechniciansTab: React.FC<TabProps> = ({ data }) => {
  const [month, setMonth] = useState(0);
  const rows = useMemo(
    () => inPeriod(data.rows, month, data.lastMonth),
    [data, month]
  );
  const period = month
    ? `en ${MONTHS[month].toLowerCase()}`
    : `de enero a ${MONTHS[data.lastMonth].toLowerCase()}`;

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Cómo lo está haciendo cada técnico con el margen y la inversión,{" "}
          {period}. Base para el variable.
        </Typography>
        <PeriodSelector
          value={month}
          lastMonth={data.lastMonth}
          onChange={setMonth}
        />
      </Stack>
      <MediaBuyingTechnicians rows={rows.filter((r) => r.type === "CM")} />
      <PaidMediaTechnicians rows={rows.filter((r) => r.type === "PM")} />
    </Stack>
  );
};

const MediaBuyingTechnicians: React.FC<{ rows: ControlBoardRow[] }> = ({
  rows,
}) => {
  const theme = useTheme();
  const toneColor = useToneColor();
  const [selected, setSelected] = useState("");
  const [selectedChannel, setSelectedChannel] = useState<
    ControlBoardChannel | ""
  >("");
  const [channelFilter, setChannelFilter] = useState<ControlBoardChannel | "">(
    ""
  );
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const withSale = rows.filter((r) => r.sale > 0);
  const techs = useMemo(
    () =>
      [...byPerson(withSale, (r) => r.technicians).entries()]
        .map(([name, list]) => ({ name, list, t: aggregate(list) }))
        .sort((a, b) => b.t.sale - a.t.sale),
    [withSale]
  );
  const channels = CM_CHANNELS.filter((c) =>
    withSale.some((r) => r.channel === c)
  );
  const average = Object.fromEntries(
    channels.map((c) => [c, aggregate(withSale.filter((r) => r.channel === c))])
  ) as Record<ControlBoardChannel, Totals>;
  const total = aggregate(withSale);

  const cell = (
    list: ControlBoardRow[],
    ref: number,
    tech?: string,
    channel?: ControlBoardChannel
  ) => {
    const t = aggregate(list);
    if (!t.sale)
      return (
        <TableCell key={tech} align="center" sx={{ color: "text.disabled" }}>
          ·
        </TableCell>
      );
    const d = t.margin - ref;
    const isSelected =
      !!tech && selected === tech && selectedChannel === channel;
    return (
      <TableCell
        key={tech}
        align="center"
        onClick={
          tech && channel
            ? () => {
                setSelected(tech);
                setSelectedChannel(isSelected ? "" : channel);
              }
            : undefined
        }
        title={`${keur(t.sale)} gestionados · ${pp(d, 1)} frente a la media`}
        sx={{
          cursor: tech ? "pointer" : "default",
          whiteSpace: "nowrap",
          boxShadow: isSelected
            ? `inset 0 0 0 2px ${theme.palette.text.primary}`
            : undefined,
          "&:hover": tech
            ? { backgroundColor: theme.palette.action.hover }
            : undefined,
        }}
      >
        <strong>{pct(t.margin, 1)}</strong>{" "}
        <Typography
          component="span"
          variant="caption"
          sx={{ fontWeight: 600, color: toneColor(toneOfMarginDiff(d)) }}
        >
          {pp(d, 1)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {keur(t.sale)}
        </Typography>
      </TableCell>
    );
  };

  const detail = techs.find((t) => t.name === selected);
  const detailRows = detail
    ? detail.list.filter(
        (r) => !selectedChannel || r.channel === selectedChannel
      )
    : [];

  return (
    <Section
      title="Técnicos de compra de medios"
      subtitle="Margen real de cada técnico frente al vendido en los briefings y frente a la media de cada formato. Pulsa una celda o una tarjeta para ver el detalle."
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Resumen
      </Typography>
      <Note>
        Margen objetivo = el vendido en los briefings de sus líneas. Real = el
        que sale de la compra. Una línea cuenta para cada técnico responsable de
        su plataforma.
      </Note>
      <Box sx={{ overflowX: "auto", mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Técnico</TableCell>
              <Num>Líneas</Num>
              <Num>Clientes</Num>
              <Num>Inversión gestionada</Num>
              <Num>Margen objetivo</Num>
              <Num>Margen real</Num>
              <Num>Diferencia</Num>
              <Num>Beneficio objetivo</Num>
              <Num>Beneficio real</Num>
              <Num>Diferencia</Num>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...techs, { name: "Total", list: withSale, t: total }].map(
              ({ name, list, t }) => {
                const isTotal = name === "Total";
                return (
                  <TableRow
                    key={name}
                    sx={isTotal ? { "& td": { fontWeight: 700 } } : undefined}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                    <Num>{new Set(list.map((r) => r.lineId)).size}</Num>
                    <Num>{clientsOf(list)}</Num>
                    <Num>{eur(t.sale)}</Num>
                    <Num muted>{pct(t.soldMargin, 1)}</Num>
                    <Num sx={{ fontWeight: 600 }}>{pct(t.margin, 1)}</Num>
                    <Num>
                      <Signal
                        text={pp(t.margin - t.soldMargin, 1)}
                        tone={toneOfMarginDiff(t.margin - t.soldMargin)}
                      />
                    </Num>
                    <Num muted>{eur(t.soldProfit)}</Num>
                    <Num>{eur(t.profitWithSoldMargin)}</Num>
                    <Num>
                      <Signal
                        text={eurSigned(t.marginOverSold)}
                        tone={toneOfAmount(t.marginOverSold, t.profit)}
                      />
                    </Num>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
        mb={1}
        gap={1}
        flexWrap="wrap"
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Margen por formato
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="tm-f">Formato</InputLabel>
          <Select
            labelId="tm-f"
            label="Formato"
            value={channelFilter}
            onChange={(e) =>
              setChannelFilter(e.target.value as ControlBoardChannel | "")
            }
          >
            <MenuItem value="">Todos los formatos</MenuItem>
            {channels.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Formato</TableCell>
              {techs.map((t) => (
                <TableCell key={t.name} align="center">
                  {t.name}
                </TableCell>
              ))}
              <TableCell align="center">Media del formato</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {channels
              .filter((c) => !channelFilter || c === channelFilter)
              .map((c) => {
                const key = `ch:${c}`;
                const isOpen = channelFilter === c || open.has(key);
                const subs = [
                  ...groupBy(
                    withSale.filter((r) => r.channel === c),
                    (r) => r.subformat
                  ).entries(),
                ]
                  .map(([s, l]) => ({ s, l, t: aggregate(l) }))
                  .sort((a, b) => b.t.sale - a.t.sale);
                return (
                  <React.Fragment key={c}>
                    <TableRow>
                      <TableCell
                        onClick={() => toggle(key)}
                        sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        <Caret open={isOpen} onClick={() => toggle(key)} />
                        <ChannelSwatch channel={c} />
                        <strong>{c}</strong>
                      </TableCell>
                      {techs.map((t) =>
                        cell(
                          t.list.filter((r) => r.channel === c),
                          average[c].margin,
                          t.name,
                          c
                        )
                      )}
                      <TableCell align="center">
                        <strong>{pct(average[c].margin, 1)}</strong>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {keur(average[c].sale)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {isOpen &&
                      subs.map((sb) => (
                        <TableRow
                          key={`${c}-${sb.s}`}
                          sx={{ backgroundColor: theme.palette.grey[50] }}
                        >
                          <TableCell sx={{ pl: 6 }}>
                            {subformatLabel(sb.s)}
                          </TableCell>
                          {techs.map((t) =>
                            React.cloneElement(
                              cell(
                                t.list.filter(
                                  (r) => r.channel === c && r.subformat === sb.s
                                ),
                                sb.t.margin
                              ),
                              { key: t.name }
                            )
                          )}
                          <TableCell align="center">
                            {pct(sb.t.margin, 1)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          mt: 3,
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        }}
      >
        {techs.map(({ name, list, t }) => (
          <ButtonBase
            key={name}
            onClick={() => {
              setSelected(selected === name && !selectedChannel ? "" : name);
              setSelectedChannel("");
            }}
            sx={{ textAlign: "left", display: "block", borderRadius: 3 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${selected === name ? theme.palette.text.primary : theme.palette.grey[200]}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {name}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {keur(t.sale)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                inversión gestionada · {new Set(list.map((r) => r.lineId)).size}{" "}
                líneas · {clientsOf(list)} clientes
              </Typography>
              <Stack gap={0.5} mt={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Vendido → real
                  </Typography>
                  <Typography variant="body2">
                    {pct(t.soldMargin, 1)} → <strong>{pct(t.margin, 1)}</strong>
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Margen del margen
                  </Typography>
                  <Signal
                    text={eurSigned(t.marginOverSold)}
                    tone={toneOfAmount(t.marginOverSold, t.profit)}
                  />
                </Stack>
              </Stack>
            </Paper>
          </ButtonBase>
        ))}
      </Box>

      {detail && (
        <Box mt={3}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Detalle de {detail.name}
              {selectedChannel ? ` · ${selectedChannel}` : ""}
            </Typography>
            <Link
              component="button"
              onClick={() => setSelected("")}
              underline="hover"
            >
              Cerrar
            </Link>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              mt: 1,
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            }}
          >
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Formato</TableCell>
                    <Num>Inversión</Num>
                    <Num>Vendido</Num>
                    <Num>Real</Num>
                    <Num>Frente a lo vendido</Num>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ...groupBy(
                      detailRows,
                      (r) => `${r.channel}|${r.subformat ?? ""}`
                    ).entries(),
                  ]
                    .map(([k, l]) => ({ k, l, t: aggregate(l) }))
                    .sort((a, b) => b.t.sale - a.t.sale)
                    .map(({ k, l, t }) => (
                      <TableRow key={k}>
                        <TableCell>
                          <ChannelSwatch channel={l[0].channel} />
                          {l[0].channel} · {subformatLabel(l[0].subformat)}
                        </TableCell>
                        <Num>{eur(t.sale)}</Num>
                        <Num muted>{pct(t.soldMargin, 1)}</Num>
                        <Num sx={{ fontWeight: 600 }}>{pct(t.margin, 1)}</Num>
                        <Num>
                          <Signal
                            text={pp(t.margin - t.soldMargin, 1)}
                            tone={toneOfMarginDiff(t.margin - t.soldMargin)}
                          />
                        </Num>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <Num>Inversión</Num>
                    <Num>Vendido</Num>
                    <Num>Real</Num>
                    <Num>Margen del margen</Num>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...groupBy(detailRows, (r) => r.client).entries()]
                    .map(([k, l]) => ({ k, t: aggregate(l) }))
                    .sort((a, b) => b.t.sale - a.t.sale)
                    .map(({ k, t }) => (
                      <TableRow key={k}>
                        <TableCell>{k}</TableCell>
                        <Num>{eur(t.sale)}</Num>
                        <Num muted>{pct(t.soldMargin, 1)}</Num>
                        <Num>
                          <Signal
                            text={pct(t.margin, 1)}
                            tone={toneOfMarginDiff(t.margin - t.soldMargin)}
                          />
                        </Num>
                        <Num>
                          <Signal
                            text={eurSigned(t.marginOverSold)}
                            tone={toneOfAmount(t.marginOverSold, t.profit)}
                          />
                        </Num>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}
    </Section>
  );
};

/** Una línea de paid media en el periodo, sumando sus meses. */
interface PaidLine {
  key: string;
  rows: ControlBoardRow[];
  planned: number;
  executed: number;
  excess: number;
}

const toPaidLines = (rows: ControlBoardRow[]): PaidLine[] =>
  [...groupBy(rows, (r) => r.lineId).entries()].map(([key, list]) => {
    const planned = sum(list, (r) => r.plannedSale);
    const executed = sum(list, (r) => r.sale);
    return {
      key,
      rows: list,
      planned,
      executed,
      excess: Math.max(0, executed - planned),
    };
  });

const toneOfExcess = (value: number) =>
  value <= 1 ? "good" : value <= 500 ? "warn" : "bad";

const PaidMediaTechnicians: React.FC<{ rows: ControlBoardRow[] }> = ({
  rows,
}) => {
  const theme = useTheme();
  const [selected, setSelected] = useState("");
  const techs = useMemo(
    () =>
      [...byPerson(rows, (r) => r.technicians).entries()]
        .map(([name, list]) => {
          const lines = toPaidLines(list);
          const excessLines = lines.filter((l) => l.excess > 1);
          return {
            name,
            list,
            t: aggregate(list),
            lines,
            excess: sum(lines, (l) => l.excess),
            unspent: sum(lines, (l) => Math.max(0, l.planned - l.executed)),
            excessLines,
            channels: [...new Set(list.map((r) => r.channel))],
          };
        })
        .sort((a, b) => b.t.sale - a.t.sale),
    [rows]
  );
  const allLines = toPaidLines(rows);
  const total = aggregate(rows);
  const over = allLines
    .filter(
      (l) =>
        l.excess > 1 && (!selected || l.rows[0].technicians.includes(selected))
    )
    .sort((a, b) => b.excess - a.excess);

  return (
    <Section
      title="Técnicos de Paid Media"
      subtitle="Inversión ejecutada frente a la prevista en el briefing y control de sobreinversión: cada euro por encima de lo previsto es inversión que no estaba vendida."
    >
      <Note>
        Exceso = lo ejecutado por encima de lo previsto, línea a línea en el
        periodo. Sin ejecutar = lo previsto que no se llegó a invertir.
      </Note>
      <Box sx={{ overflowX: "auto", mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Técnico</TableCell>
              <Num>Líneas</Num>
              <Num>Clientes</Num>
              <Num>Inversión a gestionar</Num>
              <Num>Inversión final</Num>
              <Num>Desvío</Num>
              <Num>Desvío %</Num>
              <Num>Exceso</Num>
              <Num>Líneas con exceso</Num>
              <Num>Sin ejecutar</Num>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ...techs,
              {
                name: "Total",
                list: rows,
                t: total,
                lines: allLines,
                excess: sum(allLines, (l) => l.excess),
                unspent: sum(allLines, (l) =>
                  Math.max(0, l.planned - l.executed)
                ),
                excessLines: allLines.filter((l) => l.excess > 1),
                channels: [],
              },
            ].map((o) => {
              const d = o.t.sale - o.t.plannedSale;
              const isTotal = o.name === "Total";
              return (
                <TableRow
                  key={o.name}
                  sx={isTotal ? { "& td": { fontWeight: 700 } } : undefined}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{o.name}</TableCell>
                  <Num>{o.lines.length}</Num>
                  <Num>{clientsOf(o.list)}</Num>
                  <Num muted>{eur(o.t.plannedSale)}</Num>
                  <Num sx={{ fontWeight: 600 }}>{eur(o.t.sale)}</Num>
                  <Num>{eurSigned(d)}</Num>
                  <Num>
                    {o.t.plannedSale ? (
                      <Signal
                        text={pctSigned(d / o.t.plannedSale, 1)}
                        tone={toneOfExecution(o.t.compliance)}
                      />
                    ) : (
                      "—"
                    )}
                  </Num>
                  <Num>
                    <Signal
                      text={o.excess > 1 ? eurSigned(o.excess) : "0 €"}
                      tone={toneOfExcess(o.excess)}
                    />
                  </Num>
                  <Num>{o.excessLines.length}</Num>
                  <Num muted>{eur(o.unspent)}</Num>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          mt: 3,
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        }}
      >
        {techs.map((o) => (
          <ButtonBase
            key={o.name}
            onClick={() => setSelected(selected === o.name ? "" : o.name)}
            sx={{ textAlign: "left", display: "block", borderRadius: 3 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${selected === o.name ? theme.palette.text.primary : theme.palette.grey[200]}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {o.name}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {keur(o.t.sale)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ejecutado · {o.lines.length} líneas · {o.channels.join(", ")}
              </Typography>
              <Stack gap={0.5} mt={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Ejecutado vs previsto
                  </Typography>
                  <Signal
                    text={pct(o.t.compliance, 0)}
                    tone={toneOfExecution(o.t.compliance)}
                  />
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Sobreinversión
                  </Typography>
                  <Signal
                    text={o.excess > 1 ? eurSigned(o.excess) : "0 €"}
                    tone={toneOfExcess(o.excess)}
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Líneas con exceso
                  </Typography>
                  <Typography variant="body2">
                    {o.excessLines.length} de {o.lines.length}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </ButtonBase>
        ))}
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3 }}>
        Líneas con sobreinversión{selected ? ` · ${selected}` : ""}
      </Typography>
      <Note>
        {over.length
          ? `${over.length} líneas ejecutaron más de lo previsto, ${eur(sum(over, (l) => l.excess))} en total.`
          : "Ninguna línea ha ejecutado por encima de lo previsto en el periodo."}{" "}
        {selected
          ? "Pulsa de nuevo la tarjeta para ver todos."
          : "Pulsa una tarjeta para filtrar por técnico."}
      </Note>
      {over.length > 0 && (
        <TableContainer sx={{ maxHeight: "60vh", mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Meses</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Línea</TableCell>
                <TableCell>Formato</TableCell>
                <TableCell>Técnico</TableCell>
                <Num>Previsto</Num>
                <Num>Ejecutado</Num>
                <Num>Exceso</Num>
                <Num>Exceso %</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {over.map((l) => {
                const first = l.rows[0];
                return (
                  <TableRow key={l.key} hover>
                    <TableCell>
                      {l.rows.map((r) => MONTHS_SHORT[r.month]).join(", ")}
                    </TableCell>
                    <TableCell>{first.client}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={`${first.lineName} · ${first.briefingName}`}
                    >
                      {first.lineName}
                    </TableCell>
                    <TableCell>
                      {first.channel}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {subformatLabel(first.subformat)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {first.technicians.join(", ") || "Sin asignar"}
                    </TableCell>
                    <Num muted>{eur(l.planned)}</Num>
                    <Num>{eur(l.executed)}</Num>
                    <Num>
                      <Signal
                        text={eurSigned(l.excess)}
                        tone={toneOfExcess(l.excess)}
                      />
                    </Num>
                    <Num muted>
                      {l.planned ? pctSigned(l.executed / l.planned - 1) : "—"}
                    </Num>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Section>
  );
};

export default TechniciansTab;
